import * as amplitude from "@amplitude/analytics-react-native"
import { useNavigation, useRoute } from "@react-navigation/native"
import { LinearGradient } from "expo-linear-gradient"
import React, { useContext, useEffect, useMemo, useRef } from "react"
import { ScrollView } from "react-native"
import { View } from "react-native-animatable"
import { IconButton } from "react-native-paper"
import { connect } from "react-redux"
import {
    VictoryAxis,
    VictoryBar,
    VictoryChart,
    VictoryLegend,
    VictoryScatter,
} from "victory-native"
import FilledButtonM from "../../../common/components/button/FilledButtonM"
import { SpeechBubbleM } from "../../../common/components/speech-bubble/SpeechBubbleM"
import TextM from "../../../common/components/text/TextM"
import { theme } from "../../../common/theme/theme"
import { DimensionsContext, IS_WEB } from "../../../common/utils/mobileUtils"
import { apiIdle, apiRequest } from "../../../data/redux/actions/api"
import { ChampionImageM } from "../../../student/champions/ChampionImageM"
import {
    getDiagnosticData,
    getMockDiagnosticData,
    getPageComment,
    getPerformanceRankingData,
} from "../DiagnosticHelpers"

function PerformanceRankingScreen(props) {
    const dimensions = useContext(DimensionsContext)
    const navigation = useNavigation()
    const route = useRoute()
    const page = Number(route?.params?.pageNumber) || 1
    const scrollViewRef = useRef(null)

    const diagnosticData = useMemo(() => {
        return Object.keys(props.analysisData).length
            ? getDiagnosticData(props.analysisData)
            : getMockDiagnosticData()
    }, [props.analysisData])

    const pageData = diagnosticData[page - 1]

    const data = getPerformanceRankingData(props.analysisData)

    const width =
        dimensions.width > 800
            ? dimensions.width / 2.2
            : dimensions.width - 20

    const chartHeight = data.length * 40

    const wrapSubjectName = (subject) => {
        if (!subject || subject.length <= 20) return subject

        const spaceIndex = subject.lastIndexOf(" ", 20)
        if (spaceIndex === -1) {
            return subject.substring(0, 20) + "\n" + subject.substring(20)
        }

        return (
            subject.substring(0, spaceIndex) +
            "\n" +
            subject.substring(spaceIndex + 1)
        )
    }

    const wrappedData = data.map((item) => ({
        ...item,
        wrappedSubject: wrapSubjectName(item.subject),
        subject: item.subject,
    }))

    useEffect(() => {
        if (scrollViewRef.current) {
            scrollViewRef.current.scrollTo({ x: 0, y: 0, animated: true })
        }
    }, [page])

    useEffect(() => {
        amplitude.track(`school_card_report.diagnostic_screen_${page}.see_more`)
    }, [page])

    return (
        <ScrollView
            ref={scrollViewRef}
            showsVerticalScrollIndicator={false}
            showsHorizontalScrollIndicator={false}
            horizontal={false}
            style={{ flex: 1, width: "100%" }}
            contentContainerStyle={{
                flexGrow: 1,
                alignItems: "center",
            }}
        >
            <LinearGradient
                colors={pageData.colors}
                style={{
                    flex: 1,
                    paddingTop: 20,
                    width: "100%",
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "space-between",
                }}
            >
                <View
                    style={{
                        flexDirection: "row",
                        marginBottom: 20,
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                        paddingHorizontal: 10,
                    }}
                >
                    <View style={{ width: 50, alignItems: "flex-start" }}>
                        <IconButton
                            size={32}
                            icon="times"
                            containerColor={theme.colors.transparent}
                            iconColor={theme.colors.primary}
                            onPress={() => {
                                amplitude.track(
                                    `times.school_card_report.diagnostic_screen_${page}.see_more.back`
                                )
                                navigation.goBack()
                            }}
                        />
                    </View>

                    <View style={{ flex: 1 }}>
                        <TextM
                            fontSize={32}
                            fontWeight="ExtraBold"
                            color={theme.colors.primary}
                            style={{
                                textAlign: "center",
                            }}
                            wrap
                        >
                            {page?.title || "Performance"}
                        </TextM>
                    </View>
                </View>

                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "center",
                        width,
                        marginTop: 10,
                        marginBottom: 20,
                    }}
                >
                    <ChampionImageM
                        name={pageData?.champion}
                        variant={pageData?.championVariant[pageData?.score]}
                        width={80}
                    />

                    <View style={{ flex: 1, marginLeft: 10 }}>
                        <SpeechBubbleM
                            texts={[getPageComment(pageData)]}
                            position="left"
                            hideIcons={true}
                            noAnimation={true}
                            containerStyle={{
                                flex: 1,
                            }}
                            textStyle={{
                                fontSize: 16,
                                fontFamily: "Montserrat-Regular",
                                color: theme.colors.black,
                            }}
                        />
                    </View>
                </View>

                <VictoryChart
                    width={width}
                    height={chartHeight}
                    domainPadding={{ x: [20, 20], y: 5 }}
                    padding={{ top: 20, bottom: 50, left: 160, right: 50 }}
                    horizontal
                >
                    <VictoryAxis
                        tickValues={wrappedData.map((d, i) => i + 1)}
                        tickFormat={wrappedData.map((d) => d.wrappedSubject)}
                        style={{
                            tickLabels: {
                                fontSize: 12,
                                padding: 4,
                                textAnchor: "end",
                                fontFamily: "Montserrat-Regular",
                            },
                        }}
                    />

                    <VictoryAxis
                        dependentAxis
                        style={{
                            grid: {
                                stroke: "rgba(0,0,0,0.1)",
                                strokeWidth: 0.5,
                            },
                        }}
                    />

                    <VictoryBar
                        data={wrappedData}
                        x="subject"
                        y="moyStudent"
                        cornerRadius={{ topRight: 5, topLeft: 5 }}
                        barWidth={8}
                        alignment="middle"
                        style={{
                            data: {
                                fill: ({ datum }) =>
                                    datum.moyStudent >= datum.moyClasse
                                        ? "#32CD32"
                                        : "#FF4500",
                            },
                        }}
                    />

                    <VictoryScatter
                        data={wrappedData}
                        x="subject"
                        y="moyClasse"
                        size={7}
                        symbol="star"
                        style={{
                            data: { fill: "#FFA500" },
                        }}
                    />
                </VictoryChart>

                <VictoryLegend
                    x={dimensions.width / 2 - 100}
                    itemsPerRow={1}
                    orientation="horizontal"
                    gutter={20}
                    style={{
                        labels: {
                            fontSize: 14,
                            fontFamily: "Montserrat-Regular",
                        },
                    }}
                    data={[
                        {
                            name: "Moyenne Classe",
                            symbol: { fill: "#FFA500", type: "star" },
                        },
                        {
                            name: "Moyenne Élève (Inférieure)",
                            symbol: { fill: "#FF4500", type: "circle" },
                        },
                        {
                            name: "Moyenne Élève (Supérieure)",
                            symbol: { fill: "#32CD32", type: "circle" },
                        },
                    ]}
                />

                <View
                    style={{
                        width: "100%",
                        paddingHorizontal: 20,
                        gap: 20,
                        marginTop: "auto",
                        marginBottom: 20,
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <FilledButtonM
                        onPress={() => navigation.goBack()}
                        label="Retour"
                        trackingId={`school_card_report.diagnostic_screen_${page}.see_more.back`}
                    />
                </View>
            </LinearGradient>
        </ScrollView>
    )
}

const mapStateToProps = (state) => ({
    analysisData: state.data.schoolCardReport?.analysisData || {},
})

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PerformanceRankingScreen)
